import { useEffect, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Class, IComplianceResult, LicState } from "../types";
import { getPendingClassesForUser } from "../api/Users";
import { getUserComplianceSummary } from "../api/Compliance";
import { runMyAutoArchive } from "../api/Classes";
import { getStatesForUser } from "../api/States";
import { useAuth } from '../useAuth';
import ComplianceCard from "../components/ComplianceCard";
import api from '../utils/api';
import { usePageTitle } from '../hooks/usePageTitle';
import { formatCalendarDate } from '../utils/timezone';

interface PendingPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// ── KPI Card ────────────────────────────────────────────────────────────────
interface KpiCardProps {
  label: string;
  value: string | number;
  sub?: string;
  accent?: string;
  icon?: string;
}

const KpiCard = ({ label, value, sub, accent = 'var(--color-accent)', icon }: KpiCardProps) => (
  <div style={{
    background: 'var(--color-surface-raised)',
    border: '1px solid var(--color-border)',
    borderTop: `3px solid ${accent}`,
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-card)',
    padding: '1.25rem 1.25rem 1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  }}>
    <div style={{
      fontFamily: 'var(--font-body)',
      fontSize: 'var(--fs-xs)',
      fontWeight: 'var(--fw-semibold)',
      color: 'var(--color-text-muted)',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      display: 'flex',
      alignItems: 'center',
      gap: '0.4rem',
    }}>
      {icon && <i className={`bi ${icon}`} style={{ color: accent }} />}
      {label}
    </div>
    <div style={{
      fontFamily: 'var(--font-display)',
      fontSize: 'var(--fs-4xl)',
      fontWeight: 'var(--fw-black)',
      color: 'var(--color-text)',
      lineHeight: 1.05,
    }}>
      {value}
    </div>
    {sub && (
      <div style={{
        fontFamily: 'var(--font-body)',
        fontSize: 'var(--fs-xs)',
        color: 'var(--color-text-muted)',
      }}>
        {sub}
      </div>
    )}
  </div>
);

// ── Dashboard ────────────────────────────────────────────────────────────────
const Dashboard = () => {
  usePageTitle('Dashboard');
  const { user } = useAuth();
  const location = useLocation();
  const [pendingClasses, setPendingClasses] = useState<Class[]>([]);
  const [allPendingClasses, setAllPendingClasses] = useState<Class[]>([]);
  const [complianceSummary, setComplianceSummary] = useState<IComplianceResult[]>([]);
  const [userStates, setUserStates] = useState<LicState[]>([]);
  const [hint, setHint] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [complianceLoading, setComplianceLoading] = useState<boolean>(false);
  const [statesLoading, setStatesLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [complianceError, setComplianceError] = useState<string>("");

  const [pendingPagination, setPendingPagination] = useState<PendingPagination>({
    page: 1, limit: 10, total: 0, totalPages: 0,
  });

  const [orphanedClasses, setOrphanedClasses] = useState<{ classdate: string }[]>([]);
  const [orphanedDismissed, setOrphanedDismissed] = useState<boolean>(() => {
    try {
      const key = `orphaned_dismissed_${localStorage.getItem('userId') || 'me'}`;
      const stored = localStorage.getItem(key);
      if (!stored) return false;
      return Date.now() < Number(stored);
    } catch { return false; }
  });

  const fetchPending = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError("");
    try {
      const res = await getPendingClassesForUser(user.id);
      const sortedItems = [...res.items].sort((a, b) =>
        new Date(a.classdate).getTime() - new Date(b.classdate).getTime()
      );
      setAllPendingClasses(sortedItems);
      const start = (pendingPagination.page - 1) * pendingPagination.limit;
      setPendingClasses(sortedItems.slice(start, start + pendingPagination.limit));
      setPendingPagination(prev => ({
        ...prev,
        total: sortedItems.length,
        totalPages: Math.ceil(sortedItems.length / prev.limit),
      }));
      setHint(res.hint || null);
    } catch (err) {
      console.error(err);
      setError("Failed to load pending classes.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, pendingPagination.page, pendingPagination.limit]);

  const fetchCompliance = useCallback(async () => {
    if (!user) return;
    setComplianceLoading(true);
    setComplianceError("");
    try {
      const summary = await getUserComplianceSummary(user.id);
      setComplianceSummary(summary);
    } catch (err) {
      console.error(err);
      setComplianceError("Failed to load compliance data.");
    } finally {
      setComplianceLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const fetchUserStates = useCallback(async () => {
    if (!user) return;
    setStatesLoading(true);
    try {
      const states = await getStatesForUser(user.id);
      setUserStates(states);
    } catch (err) {
      console.error(err);
    } finally {
      setStatesLoading(false);
    }
  }, [user?.id]);

  const fetchOrphanedSummary = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get('/api/classes/orphaned-summary');
      setOrphanedClasses(res.data?.classes ?? []);
      // Refresh dismissed state using user id (available now)
      const key = `orphaned_dismissed_${user.id}`;
      const stored = localStorage.getItem(key);
      if (stored && Date.now() < Number(stored)) {
        setOrphanedDismissed(true);
      }
    } catch { /* non-critical */ }
  }, [user?.id]);

  const handleDismissOrphaned = () => {
    const key = `orphaned_dismissed_${user?.id || 'me'}`;
    const expiry = Date.now() + 30 * 24 * 60 * 60 * 1000; // 30 days
    localStorage.setItem(key, String(expiry));
    setOrphanedDismissed(true);
  };

  useEffect(() => {
    fetchPending();
    fetchCompliance();
    fetchUserStates();
    fetchOrphanedSummary();
  }, [fetchPending, fetchCompliance, fetchUserStates, fetchOrphanedSummary]);

  useEffect(() => {
    if (location.pathname === '/') {
      runMyAutoArchive().catch(() => {});
      fetchCompliance();
      fetchPending();
    }
  }, [location.pathname, fetchCompliance, fetchPending]);

  const handlePageChange = (newPage: number) => {
    setPendingPagination(prev => ({ ...prev, page: newPage }));
  };

  const formatDate = (dateString: string) => {
    try {
      return formatCalendarDate(dateString);
    } catch { return dateString; }
  };

  const filteredCompliance = [...complianceSummary].sort((a, b) => {
    const aDays = typeof a.daysRemaining === 'number' ? a.daysRemaining : Infinity;
    const bDays = typeof b.daysRemaining === 'number' ? b.daysRemaining : Infinity;
    return aDays - bDays;
  });

  const totalPendingClasses = allPendingClasses.length;
  const totalPendingLUs     = Math.round(allPendingClasses.reduce((s, c) => s + (c.classLU || 0), 0) * 100) / 100;

  // KPI calculations
  const kpiEarned    = complianceSummary.reduce((s, c) => s + (c.totalEarned   || 0), 0);
  const kpiRequired  = complianceSummary.reduce((s, c) => s + (c.totalRequired || 0), 0);
  const kpiRemaining = Math.max(kpiRequired - kpiEarned, 0);
  const kpiCycles    = complianceSummary.length;
  const nextDays     = complianceSummary.length > 0
    ? Math.min(...complianceSummary.map(c => c.daysRemaining ?? Infinity))
    : null;
  const nextDaysColor = nextDays === null
    ? 'var(--color-accent)'
    : nextDays < 30
      ? 'var(--color-danger)'
      : nextDays < 60
        ? 'var(--color-accent-warm)'
        : 'var(--color-success)';

  const hasData = !complianceLoading && !complianceError && complianceSummary.length > 0;

  // Orphaned count: only classes whose date falls within the aggregated cycle window
  const minCycleTs = complianceSummary.length > 0
    ? Math.min(...complianceSummary.map(c => c.cycleStartDate ? new Date(c.cycleStartDate).getTime() : Infinity))
    : null;
  const maxCycleTs = complianceSummary.length > 0
    ? Math.max(...complianceSummary.map(c => new Date(c.cycleEndDate).getTime()))
    : null;
  const orphanedCount = (minCycleTs !== null && maxCycleTs !== null)
    ? orphanedClasses.filter(cls => {
        const t = new Date(cls.classdate).getTime();
        return t >= minCycleTs && t <= maxCycleTs;
      }).length
    : orphanedClasses.length;

  return (
    <div className="container" style={{ paddingTop: '2rem', paddingBottom: '3rem' }}>

      {/* ── Page header ── */}
      <div style={{ marginBottom: '1.75rem' }}>
        <h1 style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 'var(--fw-black)',
          fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
          color: 'var(--color-text)',
          marginBottom: '0.35rem',
          lineHeight: 1.1,
        }}>
          Welcome back, {user?.firstName || user?.email}
        </h1>
        <p style={{
          fontFamily: 'var(--font-body)',
          color: 'var(--color-text-muted)',
          fontSize: 'var(--fs-md)',
          margin: 0,
        }}>
          Your continuing education compliance overview.
        </p>
      </div>

      {/* ── Orphaned Classes Warning ── */}
      {orphanedCount > 0 && !orphanedDismissed && (
        <div style={{
          background: 'color-mix(in srgb, var(--color-accent-warm) 12%, var(--color-surface-raised))',
          border: '1px solid color-mix(in srgb, var(--color-accent-warm) 40%, transparent)',
          borderLeft: '4px solid var(--color-accent-warm)',
          borderRadius: 'var(--radius-md)',
          padding: '1rem 1.25rem',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '1rem',
          flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', flex: 1 }}>
            <i className="bi bi-exclamation-triangle-fill" style={{
              color: 'var(--color-accent-warm)',
              fontSize: '1.1rem',
              marginTop: '0.1rem',
              flexShrink: 0,
            }} />
            <div>
              <div style={{
                fontFamily: 'var(--font-body)',
                fontWeight: 'var(--fw-semibold)',
                fontSize: 'var(--fs-sm)',
                color: 'var(--color-text)',
                marginBottom: '0.2rem',
              }}>
                {orphanedCount} completed {orphanedCount === 1 ? 'class' : 'classes'} not assigned to any cycle
              </div>
              <div style={{
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--fs-xs)',
                color: 'var(--color-text-muted)',
              }}>
                These classes won't count toward compliance until added to a cycle. Review them on the Classes page.
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
            <Link
              to="/classes"
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--fs-xs)',
                fontWeight: 'var(--fw-semibold)',
                color: 'var(--color-accent-warm)',
                textDecoration: 'none',
                border: '1px solid var(--color-accent-warm)',
                borderRadius: 'var(--radius-sm)',
                padding: '0.3rem 0.65rem',
                whiteSpace: 'nowrap',
              }}
            >
              Review Classes
            </Link>
            <button
              onClick={handleDismissOrphaned}
              title="Dismiss for 30 days"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--color-text-muted)',
                fontSize: '1rem',
                padding: '0.25rem',
                lineHeight: 1,
              }}
            >
              <i className="bi bi-x" />
            </button>
          </div>
        </div>
      )}

      {/* ── KPI Stat Row ── */}
      {hasData && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem',
        }}>
          <KpiCard
            label="Credits Earned"
            value={kpiEarned}
            sub={`of ${kpiRequired} required`}
            accent="var(--color-accent)"
            icon="bi-mortarboard-fill"
          />
          <KpiCard
            label="Credits Remaining"
            value={kpiRemaining}
            sub={kpiRemaining === 0 ? 'All requirements met' : 'still needed'}
            accent={kpiRemaining === 0 ? 'var(--color-success)' : 'var(--color-accent-warm)'}
            icon="bi-hourglass-split"
          />
          <KpiCard
            label="Active Cycles"
            value={kpiCycles}
            sub={kpiCycles === 1 ? 'license cycle' : 'license cycles'}
            accent="var(--color-primary)"
            icon="bi-arrow-repeat"
          />
          {nextDays !== null && nextDays !== Infinity && (
            <KpiCard
              label="Next Deadline"
              value={nextDays < 0 ? 'Expired' : `${nextDays}d`}
              sub={nextDays < 0 ? 'cycle has expired' : nextDays < 30 ? 'urgent — act now' : 'days remaining'}
              accent={nextDaysColor}
              icon="bi-calendar-event"
            />
          )}
        </div>
      )}

      {/* ── Compliance Section ── */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
          flexWrap: 'wrap',
          gap: '0.5rem',
        }}>
          <h2 style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 'var(--fw-bold)',
            fontSize: 'var(--fs-xl)',
            margin: 0,
            color: 'var(--color-text)',
          }}>
            Compliance Status
          </h2>
          <button
            style={{
              fontFamily: 'var(--font-body)',
              fontWeight: 'var(--fw-semibold)',
              fontSize: 'var(--fs-sm)',
              padding: '0.4rem 1rem',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--color-border)',
              background: 'var(--color-surface-raised)',
              color: 'var(--color-text)',
              cursor: 'pointer',
              transition: 'border-color var(--transition-fast), color var(--transition-fast)',
            }}
            onClick={fetchCompliance}
            disabled={complianceLoading}
            onMouseEnter={e => {
              (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--color-accent)';
              (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-accent)';
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--color-border)';
              (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-text)';
            }}
          >
            {complianceLoading ? (
              <><i className="bi bi-arrow-clockwise me-1" />Refreshing…</>
            ) : (
              <><i className="bi bi-arrow-clockwise me-1" />Refresh</>
            )}
          </button>
        </div>

        {complianceLoading && (
          <div style={{ textAlign: 'center', padding: '2rem 0' }}>
            <div
              className="spinner-border"
              role="status"
              style={{ color: 'var(--color-accent)', width: '2rem', height: '2rem' }}
            >
              <span className="visually-hidden">Loading compliance data…</span>
            </div>
          </div>
        )}

        {complianceError && (
          <div className="alert alert-danger d-flex justify-content-between align-items-center">
            <span>{complianceError}</span>
            <button className="btn btn-sm btn-outline-danger" onClick={fetchCompliance}>Retry</button>
          </div>
        )}

        {!complianceLoading && !complianceError && complianceSummary.length === 0 && (
          <div className="alert alert-info">
            <i className="bi bi-info-circle me-2" />
            {statesLoading ? 'Loading…' : userStates.length === 0 ? (
              <>
                <strong>Welcome!</strong> To start tracking CE credits, first{' '}
                <Link to="/my-licenses" className="alert-link">add your license(s)</Link>, then{' '}
                <Link to="/cycles" className="alert-link">create a cycle</Link>.
              </>
            ) : (
              <>
                No active cycles found.{' '}
                <Link to="/cycles" className="alert-link">Create a cycle</Link> to start tracking.
              </>
            )}
          </div>
        )}

        {!complianceLoading && !complianceError && filteredCompliance.length > 0 && (
          <div className="row g-3">
            {filteredCompliance.map((compliance) => (
              <div key={compliance.cycleId} className="col-12 col-md-6 col-xl-3">
                <ComplianceCard compliance={compliance} showCycleLink={true} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Pending Classes ── */}
      <div style={{
        background: 'var(--color-surface-raised)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-card)',
        overflow: 'hidden',
      }}>
        {/* Card header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          padding: '0.875rem 1.25rem',
          borderBottom: '1px solid var(--color-border)',
          background: 'var(--color-primary)',
          gap: '0.5rem',
          flexWrap: 'wrap',
        }}>
          <div>
            <div style={{
              fontFamily: 'var(--font-body)',
              fontWeight: 'var(--fw-semibold)',
              fontSize: 'var(--fs-base)',
              color: 'var(--color-text-inverse)',
            }}>
              Pending Classes
            </div>
            {!loading && !error && (
              <div style={{
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--fs-xs)',
                color: 'rgba(255,255,255,0.65)',
                marginTop: '0.2rem',
              }}>
                {totalPendingClasses} {totalPendingClasses === 1 ? 'class' : 'classes'} · {totalPendingLUs} LU total
              </div>
            )}
          </div>
          <button
            onClick={fetchPending}
            disabled={loading}
            style={{
              fontFamily: 'var(--font-body)',
              fontWeight: 'var(--fw-semibold)',
              fontSize: 'var(--fs-xs)',
              padding: '0.35rem 0.9rem',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid rgba(255,255,255,0.35)',
              background: 'transparent',
              color: 'var(--color-text-inverse)',
              cursor: 'pointer',
              transition: 'background var(--transition-fast)',
            }}
            onMouseEnter={e => ((e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.12)')}
            onMouseLeave={e => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}
          >
            Refresh
          </button>
        </div>

        {loading && (
          <div style={{
            padding: '1rem 1.25rem',
            fontFamily: 'var(--font-body)',
            color: 'var(--color-text-muted)',
            fontSize: 'var(--fs-sm)',
          }}>
            Loading pending classes…
          </div>
        )}

        {error && (
          <div className="alert alert-danger m-3">
            {error}
            <button className="btn btn-sm btn-outline-danger ms-2" onClick={fetchPending}>Retry</button>
          </div>
        )}

        {hint && !loading && !error && (
          <div className="alert alert-warning m-3">{hint}</div>
        )}

        <ul className="list-group list-group-flush">
          {pendingClasses.length > 0 ? (
            pendingClasses.map((cls: Class) => {
              const isCompleted = cls.took;
              const needsAction = !isCompleted ? 'not-completed' : 'needs-cycle';

              return (
                <li
                  key={cls._id}
                  className="list-group-item"
                  style={{
                    padding: '0.875rem 1.25rem',
                    borderColor: 'var(--color-border)',
                    background: 'var(--color-surface-raised)',
                  }}
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <h6 style={{
                          margin: 0,
                          fontFamily: 'var(--font-body)',
                          fontWeight: 'var(--fw-semibold)',
                          fontSize: 'var(--fs-sm)',
                        }}>
                          <Link
                            to={`/classes/${cls._id}?returnTo=/`}
                            style={{
                              color: 'var(--color-text)',
                              textDecoration: 'none',
                              transition: 'color var(--transition-fast)',
                            }}
                            onMouseEnter={e => ((e.currentTarget as HTMLAnchorElement).style.color = 'var(--color-accent)')}
                            onMouseLeave={e => ((e.currentTarget as HTMLAnchorElement).style.color = 'var(--color-text)')}
                          >
                            {cls.classname}
                          </Link>
                        </h6>
                        {needsAction === 'not-completed' && (
                          <span style={{
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--fs-xs)',
                            fontWeight: 'var(--fw-semibold)',
                            padding: '0.2rem 0.55rem',
                            borderRadius: 'var(--radius-full)',
                            background: 'var(--color-warning-light)',
                            color: 'var(--color-accent-warm-hover)',
                            whiteSpace: 'nowrap',
                          }}>
                            Not Completed
                          </span>
                        )}
                        {needsAction === 'needs-cycle' && (
                          <span style={{
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--fs-xs)',
                            fontWeight: 'var(--fw-semibold)',
                            padding: '0.2rem 0.55rem',
                            borderRadius: 'var(--radius-full)',
                            background: 'var(--color-info-light)',
                            color: 'var(--color-accent)',
                            whiteSpace: 'nowrap',
                          }}>
                            Needs Cycle
                          </span>
                        )}
                      </div>
                      <div style={{
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--fs-xs)',
                        color: 'var(--color-text-muted)',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '0.75rem',
                        marginTop: '0.25rem',
                      }}>
                        <span>
                          <i className="bi bi-calendar me-1" />
                          {formatDate(cls.classdate)}
                          {cls.classwhen && (
                            <span className="ms-2">
                              <i className="bi bi-clock me-1" />{cls.classwhen}
                            </span>
                          )}
                        </span>
                        {cls.cycleIDs && cls.cycleIDs.length > 0 && (
                          <span>
                            <i className="bi bi-diagram-3 me-1" />
                            {cls.cycleIDs.length} {cls.cycleIDs.length === 1 ? 'cycle' : 'cycles'}
                          </span>
                        )}
                        {cls.classlink && (
                          <a
                            href={cls.classlink}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: 'var(--color-accent)', textDecoration: 'none' }}
                          >
                            <i className="bi bi-link-45deg me-1" />
                            Class Link
                          </a>
                        )}
                      </div>
                    </div>
                    <span style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 'var(--fs-xs)',
                      fontWeight: 'var(--fw-semibold)',
                      padding: '0.25rem 0.6rem',
                      borderRadius: 'var(--radius-full)',
                      background: 'var(--color-surface-muted)',
                      color: 'var(--color-text-muted)',
                      whiteSpace: 'nowrap',
                      marginLeft: '0.75rem',
                    }}>
                      {cls.classLU} LU
                    </span>
                  </div>
                </li>
              );
            })
          ) : (!loading && !error) ? (
            <li className="list-group-item text-center" style={{
              padding: '2rem',
              fontFamily: 'var(--font-body)',
              color: 'var(--color-text-muted)',
              fontSize: 'var(--fs-sm)',
            }}>
              No pending classes.
            </li>
          ) : null}
        </ul>

        {/* Pagination */}
        {pendingPagination.totalPages > 1 && !loading && !error && (
          <div style={{
            padding: '0.75rem 1.25rem',
            borderTop: '1px solid var(--color-border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '0.5rem',
          }}>
            <small style={{
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--fs-xs)',
              color: 'var(--color-text-muted)',
            }}>
              Showing {((pendingPagination.page - 1) * pendingPagination.limit) + 1}–
              {Math.min(pendingPagination.page * pendingPagination.limit, pendingPagination.total)} of {pendingPagination.total}
            </small>
            <nav aria-label="Pending classes pagination">
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${pendingPagination.page === 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => handlePageChange(pendingPagination.page - 1)} disabled={pendingPagination.page === 1}>
                    Previous
                  </button>
                </li>
                {(() => {
                  const pages = [];
                  const max = 5;
                  let start = Math.max(1, pendingPagination.page - Math.floor(max / 2));
                  let end   = Math.min(pendingPagination.totalPages, start + max - 1);
                  if (end - start < max - 1) start = Math.max(1, end - max + 1);

                  if (start > 1) {
                    pages.push(<li key="p1" className="page-item"><button className="page-link" onClick={() => handlePageChange(1)}>1</button></li>);
                    if (start > 2) pages.push(<li key="e1" className="page-item disabled"><span className="page-link">…</span></li>);
                  }
                  for (let i = start; i <= end; i++) {
                    pages.push(
                      <li key={i} className={`page-item ${pendingPagination.page === i ? 'active' : ''}`}>
                        <button className="page-link" onClick={() => handlePageChange(i)}>{i}</button>
                      </li>
                    );
                  }
                  if (end < pendingPagination.totalPages) {
                    if (end < pendingPagination.totalPages - 1) pages.push(<li key="e2" className="page-item disabled"><span className="page-link">…</span></li>);
                    pages.push(<li key={`p${pendingPagination.totalPages}`} className="page-item"><button className="page-link" onClick={() => handlePageChange(pendingPagination.totalPages)}>{pendingPagination.totalPages}</button></li>);
                  }
                  return pages;
                })()}
                <li className={`page-item ${pendingPagination.page === pendingPagination.totalPages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => handlePageChange(pendingPagination.page + 1)} disabled={pendingPagination.page === pendingPagination.totalPages}>
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
